// IND-026: orden común de las tablas de Control de planta. Pulsar la cabecera
// ordena; volver a pulsarla invierte el sentido. Los vacíos («—») van siempre
// al final, en los dos sentidos.
import { useCallback, useState } from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortValue = number | string | null | undefined;

function isEmpty(value: SortValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number') return !Number.isFinite(value);
  return value.trim() === '';
}

export function compareSortValues(a: SortValue, b: SortValue, direction: SortDirection): number {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  const factor = direction === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return factor * (a - b);
  return factor * String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

/** Copia ordenada (estable: a igualdad, se mantiene el orden de llegada). */
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, direction: SortDirection): T[] {
  return rows
    .map((row, index) => ({ row, index, key: value(row) }))
    .sort((a, b) => compareSortValues(a.key, b.key, direction) || a.index - b.index)
    .map((item) => item.row);
}

export type TableSort<K extends string> = {
  /** null: el orden de llegada (p. ej. el del backend) hasta que se pulse una cabecera. */
  key: K | null;
  direction: SortDirection;
  /** Columna nueva: empieza en `firstDirection`; la misma: invierte. */
  toggle: (key: K, firstDirection?: SortDirection) => void;
};

export function useTableSort<K extends string>(defaultKey: K, defaultDirection: SortDirection): TableSort<K> & { key: K };
export function useTableSort<K extends string>(defaultKey: K | null, defaultDirection: SortDirection): TableSort<K>;
export function useTableSort<K extends string>(defaultKey: K | null, defaultDirection: SortDirection): TableSort<K> {
  const [state, setState] = useState<{ key: K | null; direction: SortDirection }>({
    key: defaultKey,
    direction: defaultDirection,
  });
  const toggle = useCallback((key: K, firstDirection: SortDirection = 'desc') => {
    setState((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: firstDirection },
    );
  }, []);
  return { key: state.key, direction: state.direction, toggle };
}
